<template>
  <div>
    <v-toolbar flat color="white">
      <v-toolbar-title>Çok Satanlar Yönetimi</v-toolbar-title>
      <v-divider class="mx-3" inset vertical></v-divider>
      
      <v-select
        v-model="selectedTenantId"
        :items="tenantOptions"
        item-text="tenantName"
        item-value="tenantId"
        label="Tenant Seç"
        @change="onTenantChange"
        style="min-width: 150px; margin-right: 10px; margin-top: 20px;"
      ></v-select>
      
      <v-spacer></v-spacer>
      <v-btn color="primary" dark class="mb-2" @click="openAddProductDialog">
        <v-icon left>add</v-icon>
        Ürün Ekle
      </v-btn>
    </v-toolbar>

    <!-- Çok Satanlar Listesi - Sürükle Bırak -->
    <v-container fluid v-if="selectedTenantId">
      <v-row v-if="bestSellers.length === 0 && !loading">
        <v-col cols="12" class="text-center pa-8">
          <v-icon size="64" color="grey lighten-1">shopping_cart</v-icon>
          <h3 class="mt-4 grey--text">Henüz çok satan ürün eklenmemiş</h3>
          <p class="grey--text">Yukarıdaki "Ürün Ekle" butonuna tıklayarak ürün ekleyebilirsiniz.</p>
        </v-col>
      </v-row>
      
      <div class="bestseller-list">
        <div
          v-for="(item, index) in bestSellers"
          :key="item.product.id"
          class="bestseller-item"
          draggable="true"
          @dragstart="onDragStart($event, index)"
          @dragover.prevent
          @dragenter="onDragEnter($event, index)"
          @dragleave="onDragLeave($event)"
          @drop="onDrop($event, index)"
          @dragend="onDragEnd"
        >
          <div class="drag-handle">
            <span class="dot"></span><span class="dot"></span>
            <span class="dot"></span><span class="dot"></span>
            <span class="dot"></span><span class="dot"></span>
          </div>

          <div class="order-box">
            <input
              type="number"
              :value="item.product.bestSellerOrder"
              min="1"
              class="order-input"
              @change="onOrderChange(item.product, $event.target.value)"
              @click.stop
            />
          </div>

          <div class="product-thumb">
            <v-img :src="getProductImage(item.product)" contain height="48" width="48"></v-img>
          </div>

          <div class="product-info">
            <div class="product-title">{{ item.product.name }}</div>
            <div class="product-meta">
              <span class="sku-text">{{ item.product.sku }}</span>
              <span class="meta-chip" :class="item.onSale ? 'active' : 'inactive'">{{ item.onSale ? 'Aktif' : 'Pasif' }}</span>
              <span class="meta-chip stock">{{ item.product.availableStock }}</span>
            </div>
          </div>

          <div class="price-box">
            <span class="price-text">{{ formatPrice(item.product.price) }} ₺</span>
          </div>

          <button class="delete-btn" @click.stop="removeFromBestSeller(item)">
            <v-icon small color="error">delete</v-icon>
          </button>
        </div>
      </div>
    </v-container>

    <div v-else class="text-center pa-8">
      <v-icon size="64" color="grey lighten-1">store</v-icon>
      <h3 class="mt-4 grey--text">Lütfen bir Tenant seçiniz</h3>
    </div>

    <!-- Ürün Ekleme Dialog -->
    <v-dialog v-model="addProductDialog" max-width="900px" persistent scrollable>
      <v-card class="add-products-dialog">
        <!-- Üst Alan - Sabit -->
        <v-card-title class="dialog-header">
          <div class="header-content">
            <span class="headline">Çok Satanlara Ürün Ekle</span>
            <v-chip color="info" text-color="white" small class="ml-2">
              {{ filteredAvailableProducts.length }} / {{ totalAvailableProducts }} ürün
            </v-chip>
          </div>
          <v-spacer></v-spacer>
          <v-btn 
            color="success" 
            @click="addSelectedProducts" 
            :loading="addLoading" 
            :disabled="selectedProducts.length === 0"
          >
            <v-icon left>add</v-icon>
            Seçilenleri Ekle ({{ selectedProducts.length }})
          </v-btn>
        </v-card-title>
        
        <!-- Arama Alanı -->
        <div class="search-area px-4 py-2">
          <v-text-field
            v-model="productSearch"
            append-icon="search"
            label="Ürün Ara (ID, SKU, İsim)"
            single-line
            hide-details
            outlined
            dense
            clearable
            @input="searchProducts"
          ></v-text-field>
        </div>
        
        <v-divider></v-divider>

        <!-- Orta Alan - Scrollable Tablo -->
        <v-card-text class="dialog-content pa-0">
          <v-data-table
            v-model="selectedProducts"
            :headers="availableProductHeaders"
            :items="filteredAvailableProducts"
            item-key="id"
            show-select
            class="elevation-0"
            :rows-per-page-items="[10, 25, 50, { text: 'Tümü', value: -1 }]"
            :loading="productsLoading"
          >
            <template v-slot:items="props">
              <td>
                <v-checkbox v-model="props.selected" primary hide-details></v-checkbox>
              </td>
              <td class="text-xs-center">{{ props.item.id }}</td>
              <td class="text-xs-center">{{ props.item.sku }}</td>
              <td>{{ props.item.name }}</td>
              <td class="text-xs-center">{{ props.item.brand || '-' }}</td>
              <td class="text-xs-center">{{ formatPrice(props.item.price) }} ₺</td>
              <td class="text-xs-center">{{ props.item.availableStock }}</td>
              <td class="text-xs-center">
                <v-chip :color="props.item.state ? 'success' : 'error'" text-color="white" x-small>
                  {{ props.item.state ? 'Aktif' : 'Pasif' }}
                </v-chip>
              </td>
            </template>
            <template v-slot:no-data>
              <div class="text-xs-center pa-4">
                <v-icon large color="grey">search_off</v-icon>
                <p class="mt-2 grey--text">
                  {{ productsLoading ? 'Yükleniyor...' : 'Eklenebilecek ürün bulunamadı.' }}
                </p>
              </div>
            </template>
          </v-data-table>
        </v-card-text>

        <v-divider></v-divider>

        <!-- Alt Alan - Sabit -->
        <v-card-actions class="dialog-footer">
          <v-btn 
            color="warning" 
            flat 
            @click="selectAllFiltered"
            :disabled="filteredAvailableProducts.length === 0"
          >
            <v-icon left>select_all</v-icon>
            Tümünü Seç
          </v-btn>
          <v-btn 
            flat 
            @click="clearSelection"
            :disabled="selectedProducts.length === 0"
          >
            <v-icon left>deselect</v-icon>
            Seçimi Temizle
          </v-btn>
          <v-spacer></v-spacer>
          <v-btn color="blue darken-1" flat @click="closeAddProductDialog">
            <v-icon left>close</v-icon>
            Kapat
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
export default {
  data: () => ({
    selectedTenantId: null,
    tenantOptions: [
      { tenantId: 1, tenantName: "Altinda Firsat" },
      { tenantId: 2, tenantName: "EAltin" }
    ],
    loading: false,
    addLoading: false,
    productsLoading: false,
    addProductDialog: false,
    selectedProducts: [],
    productSearch: '',
    draggedIndex: null,
    isDragging: false,
    imagePreviewUrls: {},
    availableProductHeaders: [
      { text: "Seç", value: "data-table-select" },
      { text: "Id", value: "id" },
      { text: "SKU", value: "sku" },
      { text: "Ürün Adı", value: "name" },
      { text: "Marka", value: "brand" },
      { text: "Fiyat", value: "price" },
      { text: "Stok", value: "availableStock" },
      { text: "Durum", value: "state" }
    ]
  }),

  computed: {
    bestSellers() {
      return this.$store.state.products.bestSellers || [];
    },
    availableProducts() {
      return this.$store.state.products.availableForBestSeller || [];
    },
    totalAvailableProducts() {
      return this.availableProducts.length;
    },
    filteredAvailableProducts() {
      if (!this.productSearch || this.productSearch.trim() === '') {
        return this.availableProducts;
      }
      
      const searchTerm = this.productSearch.toLowerCase().trim();
      
      return this.availableProducts.filter(item => {
        const id = item.id ? item.id.toString() : '';
        const sku = item.sku ? item.sku.toLowerCase() : '';
        const name = item.name ? item.name.toLowerCase() : '';
        
        return id.includes(searchTerm) || 
               sku.includes(searchTerm) || 
               name.includes(searchTerm);
      });
    }
  },

  mounted() {
    const savedTenantId = localStorage.getItem('selectedTenantId');
    if (savedTenantId && savedTenantId !== '0') {
      this.selectedTenantId = parseInt(savedTenantId);
      this.initialize();
    } else {
      this.$store.commit('products/clearBestSellers');
    }
  },

  beforeDestroy() {
  },

  methods: {
    getBestSellerProductIds(items) {
      return (items || [])
        .map(x => (x && x.product ? x.product.id : null))
        .filter(id => Number.isInteger(id));
    },

    onTenantChange() {
      if (this.selectedTenantId) {
        localStorage.setItem('selectedTenantId', this.selectedTenantId.toString());
        localStorage.setItem('selectedMpId', '5');
        this.$store.commit('products/clearBestSellers');
        this.imagePreviewUrls = {};
        this.initialize();
      } else {
        this.$store.commit('products/clearBestSellers');
        this.imagePreviewUrls = {};
      }
    },

    async initialize() {
      if (!this.selectedTenantId) {
        this.$store.commit('products/clearBestSellers');
        this.imagePreviewUrls = {};
        return;
      }
      
      localStorage.setItem('selectedMpId', '5');
      
      this.loading = true;
      try {
        await this.$store.dispatch("products/getBestSellers");
        await this.$nextTick();
        await this.loadImagePreviews();
      } catch (error) {
        console.error('Initialize error:', error);
        this.$store.commit('products/clearBestSellers');
        this.imagePreviewUrls = {};
      } finally {
        this.loading = false;
      }
    },

    async loadImagePreviews() {
      const Api = await import('@/api/baseApi').then(m => m.default);
      this.imagePreviewUrls = {};
      
      if (!this.bestSellers || this.bestSellers.length === 0) {
        return;
      }
      
      for (const item of this.bestSellers) {
        const product = item.product;
        if (!product) continue;
        
        const productId = product.id;
        
        if (product.images) {
          try {
            const images = JSON.parse(product.images);
            if (images && images.length > 0) {
              const imageUrl = images[0];
              if (imageUrl.startsWith('http://') || imageUrl.startsWith('https://') || imageUrl.startsWith('//')) {
                this.imagePreviewUrls[productId] = imageUrl;
              } else {
                const response = await Api.post('/api/upload/presigned-download-url', {
                  fileKey: imageUrl
                });
                this.imagePreviewUrls[productId] = response.data.downloadUrl;
              }
            }
          } catch (error) {
          }
        }
      }
      
      this.$forceUpdate();
    },

    getProductImage(item) {
      const productId = item.id;
      if (this.imagePreviewUrls[productId]) {
        return this.imagePreviewUrls[productId];
      }
      
      return 'data:image/svg+xml;charset=UTF-8,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20width%3D%22200%22%20height%3D%22200%22%3E%3Crect%20fill%3D%22%23f0f0f0%22%20width%3D%22200%22%20height%3D%22200%22%2F%3E%3Ctext%20x%3D%22100%22%20y%3D%22100%22%20font-size%3D%2220%22%20text-anchor%3D%22middle%22%20fill%3D%22%23999%22%3ENo%20Image%3C%2Ftext%3E%3C%2Fsvg%3E';
    },

    formatPrice(price) {
      return price ? price.toFixed(2) : '0.00';
    },

    async openAddProductDialog() {
      if (!this.selectedTenantId) {
        alert("Lütfen önce bir Tenant seçiniz!");
        return;
      }
      
      this.addProductDialog = true;
      this.selectedProducts = [];
      this.productSearch = '';
      
      await this.loadAvailableProducts();
    },

    async loadAvailableProducts() {
      this.productsLoading = true;
      try {
        await this.$store.dispatch("products/getAvailableProductsForBestSeller");
      } catch (error) {
        console.error('Load available products error:', error);
      } finally {
        this.productsLoading = false;
      }
    },

    searchProducts() {
    },

    selectAllFiltered() {
      this.selectedProducts = [...this.filteredAvailableProducts];
    },

    clearSelection() {
      this.selectedProducts = [];
    },

    closeAddProductDialog() {
      this.addProductDialog = false;
      this.productSearch = '';
      this.selectedProducts = [];
    },

    async addSelectedProducts() {
      if (this.selectedProducts.length === 0) {
        alert('Lütfen en az bir ürün seçin!');
        return;
      }
      
      this.addLoading = true;
      try {
        const productIds = this.selectedProducts.map(p => p.id);
        await this.$store.dispatch("products/addToBestSellers", productIds);
        this.addProductDialog = false;
        this.$store.commit('SET_MESSAGE', 'Seçilen ürünler başarıyla eklendi!');
        await this.loadImagePreviews();
      } catch (error) {
        console.error('Add to bestsellers error:', error);
        this.$store.commit('SET_MESSAGE', 'Ürün ekleme sırasında hata oluştu!');
      } finally {
        this.addLoading = false;
      }
    },

    async removeFromBestSeller(item) {
      if (confirm(`"${item.product.name}" ürününü çok satanlardan çıkarmak istediğinize emin misiniz?`)) {
        try {
          await this.$store.dispatch("products/removeFromBestSeller", item.product.id);
          this.$store.commit('SET_MESSAGE', 'Ürün çok satanlardan çıkarıldı!');
        } catch (error) {
          console.error('Remove from bestseller error:', error);
          this.$store.commit('SET_MESSAGE', 'Ürün çıkarılırken hata oluştu!');
        }
      }
    },

    async onOrderChange(item, newOrder) {
      const order = parseInt(newOrder);
      if (isNaN(order) || order < 1) return;
      if (order === item.bestSellerOrder) return;
      
      const items = [...this.bestSellers];
      const currentIndex = items.findIndex(i => i && i.product && i.product.id === item.id);
      const targetIndex = order - 1;
      
      if (targetIndex < 0 || targetIndex >= items.length) {
        this.$store.commit('SET_MESSAGE', 'Geçersiz sıra numarası!');
        return;
      }
      
      const movedItem = items.splice(currentIndex, 1)[0];
      items.splice(targetIndex, 0, movedItem);
      
      const newOrderIds = this.getBestSellerProductIds(items);
      if (newOrderIds.length !== items.length) {
        this.$store.commit('SET_MESSAGE', 'Sıralama güncellenemedi: ürün bilgisi eksik (id). Sayfa yenileniyor...');
        await this.$store.dispatch("products/getBestSellers");
        return;
      }
      
      try {
        await this.$store.dispatch("products/updateBestSellerOrder", newOrderIds);
        this.$store.commit('SET_MESSAGE', 'Sıralama güncellendi!');
      } catch (error) {
        console.error('Order change error:', error);
        await this.$store.dispatch("products/getBestSellers");
        this.$store.commit('SET_MESSAGE', 'Sıralama güncellenirken hata oluştu!');
      }
    },

    onDragStart(event, index) {
      this.draggedIndex = index;
      this.isDragging = true;
      event.dataTransfer.effectAllowed = 'move';
      event.target.style.opacity = '0.5';
    },

    onDragEnter(event, index) {
      if (this.draggedIndex !== null && this.draggedIndex !== index) {
        const item = event.target.closest('.bestseller-item');
        if (item) {
          item.classList.add('drag-over');
        }
      }
    },

    onDragLeave(event) {
      const item = event.target.closest('.bestseller-item');
      if (item) {
        item.classList.remove('drag-over');
      }
    },

    async onDrop(event, dropIndex) {
      event.preventDefault();
      const item = event.target.closest('.bestseller-item');
      if (item) {
        item.classList.remove('drag-over');
      }
      
      if (this.draggedIndex !== null && this.draggedIndex !== dropIndex) {
        const items = [...this.bestSellers];
        const draggedItem = items[this.draggedIndex];
        
        items.splice(this.draggedIndex, 1);
        items.splice(dropIndex, 0, draggedItem);
        
        const newOrder = this.getBestSellerProductIds(items);
        if (newOrder.length !== items.length) {
          this.$store.commit('SET_MESSAGE', 'Sıralama güncellenemedi: ürün bilgisi eksik (id). Sayfa yenileniyor...');
          await this.$store.dispatch("products/getBestSellers");
          this.draggedIndex = null;
          this.isDragging = false;
          return;
        }
        
        try {
          await this.$store.dispatch("products/updateBestSellerOrder", newOrder);
          this.$store.commit('SET_MESSAGE', 'Sıralama güncellendi!');
        } catch (error) {
          console.error('Reorder error:', error);
          await this.$store.dispatch("products/getBestSellers");
          this.$store.commit('SET_MESSAGE', 'Sıralama güncellenirken hata oluştu!');
        }
      }
      
      this.draggedIndex = null;
      this.isDragging = false;
    },

    onDragEnd(event) {
      event.target.style.opacity = '1';
      this.isDragging = false;
      const items = document.querySelectorAll('.bestseller-item');
      items.forEach(item => {
        item.classList.remove('drag-over');
      });
    }
  }
};
</script>

<style scoped>
.bestseller-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 8px 0;
}

.bestseller-item {
  display: flex;
  align-items: center;
  background: #f8fafc;
  border-radius: 12px;
  padding: 12px 16px;
  cursor: grab;
  transition: all 0.2s ease;
  border: 2px solid transparent;
}

.bestseller-item:hover {
  background: #e8eef5;
}

.bestseller-item:active {
  cursor: grabbing;
}

.bestseller-item.drag-over {
  background: #d4e4f7;
  border: 2px dashed #5b8bd4;
  transform: scale(1.01);
}

.drag-handle {
  display: grid;
  grid-template-columns: repeat(2, 6px);
  gap: 4px;
  padding: 8px;
  margin-right: 12px;
  cursor: grab;
  opacity: 0.4;
  transition: opacity 0.2s;
}

.bestseller-item:hover .drag-handle {
  opacity: 0.7;
}

.drag-handle:active {
  cursor: grabbing;
}

.drag-handle .dot {
  width: 6px;
  height: 6px;
  background: #8fa4bf;
  border-radius: 50%;
}

.order-box {
  margin-right: 16px;
}

.order-input {
  width: 40px;
  height: 32px;
  border: 1px solid #d1d9e6;
  border-radius: 6px;
  text-align: center;
  font-size: 14px;
  font-weight: 500;
  color: #374151;
  background: white;
  outline: none;
  transition: border-color 0.2s;
}

.order-input:focus {
  border-color: #5b8bd4;
}

.order-input::-webkit-inner-spin-button,
.order-input::-webkit-outer-spin-button {
  -webkit-appearance: none;
  margin: 0;
}

.product-thumb {
  width: 48px;
  height: 48px;
  min-width: 48px;
  background: #e8eef5;
  border-radius: 8px;
  margin-right: 16px;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
}

.product-info {
  flex: 1;
  min-width: 0;
}

.product-title {
  font-weight: 600;
  font-size: 14px;
  color: #1f2937;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  margin-bottom: 4px;
}

.product-meta {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}

.sku-text {
  font-size: 12px;
  color: #6b7280;
}

.meta-chip {
  font-size: 10px;
  padding: 2px 8px;
  border-radius: 4px;
  font-weight: 600;
}

.meta-chip.active {
  background-color: #dcfce7;
  color: #166534;
}

.meta-chip.inactive {
  background-color: #fee2e2;
  color: #991b1b;
}

.meta-chip.stock {
  background-color: #dbeafe;
  color: #1e40af;
}

.price-box {
  margin: 0 16px;
}

.price-text {
  font-size: 14px;
  font-weight: 600;
  color: #059669;
  white-space: nowrap;
}

.delete-btn {
  width: 36px;
  height: 36px;
  border: none;
  background: transparent;
  border-radius: 8px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background-color 0.2s;
}

.delete-btn:hover {
  background: #fee2e2;
}

/* Dialog Stilleri */
.add-products-dialog {
  display: flex;
  flex-direction: column;
  max-height: 85vh;
}

.dialog-header {
  flex-shrink: 0;
  background-color: #f5f5f5;
  border-bottom: 1px solid #e0e0e0;
}

.dialog-header .header-content {
  display: flex;
  align-items: center;
}

.search-area {
  flex-shrink: 0;
  background-color: #fafafa;
}

.dialog-content {
  flex: 1;
  overflow-y: auto;
  min-height: 300px;
  max-height: 50vh;
}

.dialog-footer {
  flex-shrink: 0;
  background-color: #f5f5f5;
  border-top: 1px solid #e0e0e0;
}
</style>

